import React, { useState, useContext } from "react";
import "./scss/styles/app.scss";
import SearchResultPage from "./views/SearchResultPage";
import MainPage from "./views/MainPage";
import SearchPage from "./views/SearchPage";
import DesignDetailsPage from "./views/DesignDetailsPage";
import { SurveyModal } from "./components/common/SurveyModal";
import Masonry from "./views/MasonryTest";
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
} from "react-router-dom";
import useEndpoint from "./customHooks/useEndpoint";
import useLocalStorage from "./customHooks/useLocalStorage";
import { initialSearchParams } from "./constants/constants";
import { I18nContext } from "./i18n";
require("dotenv").config();

const baseUrl = process.env.REACT_APP_API_URL;
const buildTime = process.env.REACT_APP_BUILD_TIME;

const meta = document.createElement("meta");
meta.name = "bt";
meta.content = new Date(buildTime * 1000);
document.head.appendChild(meta);

function buildRoutePath(path) {
  const noLangInPath = !/\/:lang\??(\/|$)/.test(path);
  const convPath = `/:basePath?${noLangInPath ? "/:lang" : ""}${path}/`;
  return convPath;
}

function postSearchEndpoint() {
  /* eslint-disable react-hooks/rules-of-hooks */
  return useEndpoint((data, path) => ({
    url: baseUrl + "/" + path,
    method: "POST",
    data,
  }));
}

function getSearchEndpoint() {
  /* eslint-disable react-hooks/rules-of-hooks */
  return useEndpoint((path) => ({
    url: baseUrl + "/" + path,
    method: "GET",
  }));
}

const App = () => {
  const [searchParams, setSearchParams] = useState(initialSearchParams);
  const [viewMode, setViewMode] = useState("prhCard");
  const [currentPage, setCurrentPage] = useState(1);
  const [masonryPage, setMasonryPage] = useState(1);
  const [activeLink, setActiveLink] = useLocalStorage("activeLink", "Home");
  const [showSurvey, setShowSurvey] = useState(true);

  const closeSurvey = () => {
    setShowSurvey(false);
  };

  const onChangePage = (page, paginationFor) => {
    if (paginationFor === "masonry") {
      setMasonryPage(page);
    } else {
      setCurrentPage(page);
    }
  };

  const [search, postSearch] = postSearchEndpoint();
  function doSearch(params, path) {
    if (viewMode === "masonry") {
      setViewMode("prhCard");
    }
    setCurrentPage(1);
    setMasonryPage(1);
    postSearch(params, path);
  }

  const [design, getDesign] = getSearchEndpoint();
  function doDesignSearch(path) {
    getDesign(path);
  }

  const withSlashOrEmpty = (param) => {
    return param ? "/" + param : "";
  };

  const supportedRoutes = ["search", "results", "design"];
  const isSupportedRoute = (route) => supportedRoutes.includes(route);

  const supportedLanguages = ["fi", "sv", "en"];
  const isSupportedLanguage = (lang) => supportedLanguages.includes(lang);

  let basePath = null,
    lang = null,
    route = null;

  const { dispatch } = useContext(I18nContext);

  const setPathParams = (params) => {
    lang = isSupportedLanguage(params.lang)
      ? params.lang
      : isSupportedLanguage(params.basePath)
        ? params.basePath
        : null;

    route = isSupportedRoute(params.lang)
      ? params.lang
      : isSupportedRoute(params.basePath)
        ? params.basePath
        : null;

    basePath =
      isSupportedLanguage(params.lang) ||
        (isSupportedRoute(params.lang) &&
          !isSupportedLanguage(params.basePath)) ||
        (params.basePath &&
          !isSupportedLanguage(params.basePath) &&
          !isSupportedRoute(params.basePath))
        ? params.basePath
        : !params.basePath && !isSupportedLanguage(params.lang)
          ? params.lang
          : null;
  };

  const determineRedirect = (props) => {
    if (!route && lang) return false;

    let pathRemainder = props.location.pathname;

    for (const pathParam of [lang, basePath, route])
      pathRemainder = pathRemainder.replace("/" + pathParam, "");

    return (
      <Redirect
        to={`${withSlashOrEmpty(basePath)}/${lang || "fi"}${withSlashOrEmpty(
          route
        )}${pathRemainder}`}
      />
    );
  };

  const pathParams = {
    getLang: () => lang,

    buildPath: (path) =>
      `${withSlashOrEmpty(basePath)}${withSlashOrEmpty(lang)}${withSlashOrEmpty(
        path
      )}/`,

    updateLanguage: () => {
      if (lang !== "fi" && document.documentElement.lang !== lang) {
        document.documentElement.lang = lang;
        dispatch({ type: "setLanguage", payload: lang });
      }
    },
  };

  return (
    <div>
      <SurveyModal lang={document.documentElement.lang} />
      <Router>
        <Switch>
          <Route
            path={buildRoutePath("/search")}
            render={(props) => {
              setPathParams(props.match.params);

              return (
                determineRedirect(props) || (
                  <SearchPage
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    fetchSearchResults={doSearch}
                    activeLink={activeLink}
                    setActiveLink={setActiveLink}
                    pathParams={pathParams}
                    closeSurvey={closeSurvey}
                    showSurvey={showSurvey}
                  />
                )
              );
            }}
          />
          <Route
            path={buildRoutePath("/results")}
            render={(props) => {
              setPathParams(props.match.params);

              return (
                determineRedirect(props) || (
                  <SearchResultPage
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    searchResult={search}
                    fetchSearchResults={doSearch}
                    path="design"
                    onChangePage={onChangePage}
                    currentPage={currentPage}
                    masonryPage={masonryPage}
                    activeLink={activeLink}
                    pathParams={pathParams}
                    closeSurvey={closeSurvey}
                    showSurvey={showSurvey}
                    {...props}
                  />
                )
              );
            }}
          />
          <Route
            path="/masonry"
            render={() => <Masonry pathParams={pathParams} />}
          />
          <Route
            exact
            path={buildRoutePath(
              "/design/:applicationNumber/:registrationNumber1?/:registrationNumber2?"
            )}
            render={(props) => {
              setPathParams(props.match.params);

              return (
                determineRedirect(props) || (
                  <DesignDetailsPage
                    fetchDesign={doDesignSearch}
                    design={design}
                    activeLink={activeLink}
                    pathParams={pathParams}
                  />
                )
              );
            }}
          />
          <Route
            path={buildRoutePath("/:lang?")}
            render={(props) => {
              setPathParams(props.match.params);
              const redirect = determineRedirect(props);

              if (redirect) return redirect;
              else if (isSupportedLanguage(lang))
                return (
                  <MainPage
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    fetchSearchResults={doSearch}
                    activeLink={activeLink}
                    setActiveLink={setActiveLink}
                    pathParams={pathParams}
                    closeSurvey={closeSurvey}
                    showSurvey={showSurvey}
                  />
                );
              else
                return (
                  <Redirect
                    to={`${withSlashOrEmpty(basePath)}/${document.documentElement.lang
                      }/`}
                  />
                );
            }}
          />
        </Switch>
      </Router>
    </div>
  );
};

export default App;
